
import React from 'react';
import { Button } from "@/components/ui/button";
import { Play, Pause, SkipBack, SkipForward, Plus, Layers } from "lucide-react";
import { Project } from '@/hooks/useProjects';
import { useProjectRrwebSessions } from '@/hooks/useProjectRrwebSessions';
import { useProjectSlides } from '@/hooks/useProjectSlides';
import { useProjectAudioElements } from '@/hooks/useProjectAudio';
import TimelineEditor from './timeline/TimelineEditor';

interface TimelineProps {
  project: Project;
}

const Timeline = ({ project }: TimelineProps) => {
  const { data: rrwebSessions } = useProjectRrwebSessions(project.id);
  const { data: slides } = useProjectSlides(project.id);
  const { data: audioElements } = useProjectAudioElements(project.id);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(false);

  const totalElements = (rrwebSessions?.length || 0) + (slides?.length || 0) + (audioElements?.length || 0);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    console.log('Timeline playback toggled:', !isPlaying);
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-64 bg-editor-medium border-t border-editor-light flex flex-col">
      {/* Timeline Header */}
      <div className="flex items-center justify-between p-4 border-b border-editor-light">
        <div className="flex items-center space-x-4">
          <h3 className="font-semibold flex items-center">
            <Layers className="h-4 w-4 mr-2" />
            Timeline - {project.title}
          </h3>
          <div className="text-sm text-gray-400">
            {totalElements} elements
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button size="sm" variant="ghost" onClick={() => setCurrentTime(0)}>
            <SkipBack className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={handlePlayPause}>
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="ghost">
            <SkipForward className="h-4 w-4" />
          </Button>
          <div className="ml-4 text-sm text-gray-300">
            {formatTime(currentTime)}
          </div>
        </div>
      </div>

      {/* Timeline Editor */}
      <div className="flex-1 overflow-hidden">
        <TimelineEditor 
          project={project}
          rrwebSessions={rrwebSessions || []}
          slides={slides || []}
          audioElements={audioElements || []}
          currentTime={currentTime}
          onTimeChange={setCurrentTime}
        />
      </div>

      {/* Timeline Footer */}
      <div className="p-2 border-t border-editor-light bg-editor-dark">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div>
            Created: {new Date(project.created_at).toLocaleDateString()}
            {project.last_edited_at && (
              <span className="ml-4">
                Last edited: {new Date(project.last_edited_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <span>Duration: {formatTime(0)}</span>
            <Button size="sm" variant="ghost" className="h-6 px-2">
              <Plus className="h-3 w-3 mr-1" />
              Add Element
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Timeline;
